import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Globe,
  PlugZap,
  RefreshCw,
  Search,
  Signal,
  Unlink,
  X,
} from "lucide-react"
import {
  smartoltService,
  type SmartOLTOltRow,
  type SmartOLTOnuRow,
} from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import {
  compareRows,
  displayName,
  formatAuthDate,
  formatSignal,
  hasService,
  isServiceOn,
  signalBand,
  statusKind,
  wanLabel,
  type PonFilter,
  type SignalBand,
  type SortKey,
  type StatusFilter,
} from "./smartoltConfiguredUtils.ts"

const PAGE_SIZES = [10, 25, 50, 100]
const selectClass = "h-9 min-w-[96px] rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-800 shadow-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
type DbLinkFilter = "" | "linked" | "not_in_db"

function countMap(values: Array<string | number | null | undefined>) {
  const counts = new Map<string, number>()
  values.forEach((value) => {
    if (value == null || value === "") return
    const key = String(value)
    counts.set(key, (counts.get(key) || 0) + 1)
  })
  return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
}

type FilterKey = "olt" | "board" | "port" | "zone" | "odb" | "vlan" | "type" | "db" | "pon" | "status" | "signal"

function rowMatchesFilters(
  row: SmartOLTOnuRow,
  filters: {
    search: string
    oltFilter: string
    boardFilter: string
    portFilter: string
    zoneFilter: string
    odbFilter: string
    vlanFilter: string
    typeFilter: string
    dbLinkFilter: DbLinkFilter
    ponFilter: PonFilter
    statusFilter: StatusFilter
    signalFilters: SignalBand[]
  },
  except?: FilterKey,
) {
  if (except !== "olt" && filters.oltFilter && String(row.olt_id) !== filters.oltFilter) return false
  if (except !== "board" && filters.boardFilter && String(row.board) !== filters.boardFilter) return false
  if (except !== "port" && filters.portFilter && String(row.port) !== filters.portFilter) return false
  if (except !== "zone" && filters.zoneFilter && String(row.zone_name || "") !== filters.zoneFilter) return false
  if (except !== "odb" && filters.odbFilter && String(row.odb_name || "") !== filters.odbFilter) return false
  if (except !== "vlan" && filters.vlanFilter && String(row.vlan ?? "") !== filters.vlanFilter) return false
  if (except !== "type" && filters.typeFilter && String(row.onu_type_name || "") !== filters.typeFilter) return false
  if (except !== "db" && filters.dbLinkFilter === "linked" && !row.is_in_db) return false
  if (except !== "db" && filters.dbLinkFilter === "not_in_db" && row.is_in_db) return false
  if (except !== "pon" && filters.ponFilter && String(row.pon_type || "").toLowerCase() !== filters.ponFilter) return false
  if (except !== "status" && filters.statusFilter && statusKind(row) !== filters.statusFilter) return false
  if (except !== "signal" && filters.signalFilters.length > 0) {
    const band = signalBand(row.signal_rx)
    if (!band || !filters.signalFilters.includes(band)) return false
  }
  const q = filters.search.trim().toLowerCase()
  if (!q) return true
  const haystack = [
    displayName(row),
    row.onu_external_id,
    row.sn,
    row.mac,
    row.olt_name,
    row.zone_name,
    row.odb_name,
    row.onu_type_name,
    row.vlan,
    row.mgmt_ip,
    row.customer_internet_id,
    row.customer_name,
    row.is_in_db ? "db linked in db database linked" : "not in db unmapped unlinked",
  ].filter(Boolean).join(" ").toLowerCase()
  return haystack.includes(q)
}

function exportCsv(rows: SmartOLTOnuRow[]) {
  const header = [
    "Status",
    "DB Link",
    "Name",
    "SN/MAC",
    "Zone",
    "Signal",
    "TR069",
    "Mode",
    "VLAN",
    "VoIP",
    "TV",
    "Type",
    "Auth Date",
    "PON Port",
    "Management IP",
    "M-VLAN",
    "WAN",
  ]
  const lines = rows.map((row) =>
    [
      statusKind(row),
      row.is_in_db ? `DB Linked${row.customer_internet_id ? ` (${row.customer_internet_id})` : ""}` : "Not in DB",
      displayName(row),
      row.sn || row.mac || "",
      row.zone_name || "",
      formatSignal(row.signal_rx),
      row.tr069 || "",
      wanLabel(row.wan_mode),
      row.vlan ?? "",
      row.voip ?? "",
      row.catv_status ?? "",
      row.onu_type_name || "",
      row.auth_date || "",
      row.pon_port || `${row.board || ""}/${row.port || ""}`,
      row.mgmt_ip || "",
      row.mgmt_vlan ?? "",
      row.wan_mode || "",
    ]
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(","),
  )
  const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = "configured-onus.csv"
  link.click()
  URL.revokeObjectURL(url)
}

function StatusIcon({ kind }: { kind: ReturnType<typeof statusKind> }) {
  if (kind === "online") return <Globe className="h-4 w-4 text-emerald-600" aria-label="Online" />
  if (kind === "los") return <Unlink className="h-4 w-4 text-rose-600" aria-label="LOS" />
  if (kind === "power") return <PlugZap className="h-4 w-4 text-amber-600" aria-label="Power fail" />
  return <Globe className="h-4 w-4 text-slate-400" aria-label="Offline" />
}

function SignalCell({ value }: { value: number | string | null | undefined }) {
  const band = signalBand(value)
  const color =
    band === "strong" ? "text-emerald-600" : band === "medium" ? "text-amber-600" : band === "weak" ? "text-rose-600" : "text-slate-400"
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-medium">
      <Signal className={`h-3.5 w-3.5 ${color}`} />
      <span className={color}>{formatSignal(value)}</span>
    </span>
  )
}

function Badge({ children, tone }: { children: string; tone: "green" | "red" | "slate" }) {
  const cls =
    tone === "green"
      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
      : tone === "red"
        ? "bg-rose-50 text-rose-700 border border-rose-200"
        : "bg-slate-100 text-slate-700 border border-slate-200"
  return <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${cls}`}>{children}</span>
}

export function SmartOLTConfiguredPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [syncingMapping, setSyncingMapping] = useState(false)
  const [rows, setRows] = useState<SmartOLTOnuRow[]>([])
  const [olts, setOlts] = useState<SmartOLTOltRow[]>([])
  const [search, setSearch] = useState(() => searchParams.get("q") || "")
  const [oltFilter, setOltFilter] = useState(() => searchParams.get("olt") || "")
  const [boardFilter, setBoardFilter] = useState(() => searchParams.get("board") || "")
  const [portFilter, setPortFilter] = useState(() => searchParams.get("port") || "")
  const [zoneFilter, setZoneFilter] = useState(() => searchParams.get("zone") || "")
  const [odbFilter, setOdbFilter] = useState(() => searchParams.get("odb") || "")
  const [vlanFilter, setVlanFilter] = useState(() => searchParams.get("vlan") || "")
  const [typeFilter, setTypeFilter] = useState(() => searchParams.get("type") || "")
  const [dbLinkFilter, setDbLinkFilter] = useState<DbLinkFilter>(() => {
    const value = searchParams.get("db")
    return value === "linked" || value === "not_in_db" ? value : ""
  })
  const [ponFilter, setPonFilter] = useState<PonFilter>(() => (searchParams.get("pon") as PonFilter) || "")
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => (searchParams.get("status") as StatusFilter) || "")
  const [signalFilters, setSignalFilters] = useState<SignalBand[]>(() => {
    const value = searchParams.get("signal") as SignalBand | null
    return value === "strong" || value === "medium" || value === "weak" ? [value] : []
  })
  const [sortKey, setSortKey] = useState<SortKey>("name")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const navigate = useNavigate()

  const load = async (silent = false, sync = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      if (sync) {
        try {
          await smartoltService.runSyncNow(5)
        } catch {
          /* keep listing even if sync is busy */
        }
      }
      const [onuRows, oltRows] = await Promise.all([
        smartoltService.getOnus({ limit: 5000 }),
        smartoltService.getOlts(),
      ])
      setRows(Array.isArray(onuRows) ? onuRows : [])
      setOlts(Array.isArray(oltRows) ? oltRows : [])
    } catch {
      toast.error("Failed to load configured ONUs")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load()
    const id = setInterval(() => load(true), 30000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    setPage(1)
  }, [search, oltFilter, boardFilter, portFilter, zoneFilter, odbFilter, vlanFilter, typeFilter, dbLinkFilter, ponFilter, statusFilter, signalFilters, pageSize])

  useEffect(() => {
    const next = new URLSearchParams()
    if (search) next.set("q", search)
    if (oltFilter) next.set("olt", oltFilter)
    if (boardFilter) next.set("board", boardFilter)
    if (portFilter) next.set("port", portFilter)
    if (zoneFilter) next.set("zone", zoneFilter)
    if (odbFilter) next.set("odb", odbFilter)
    if (vlanFilter) next.set("vlan", vlanFilter)
    if (typeFilter) next.set("type", typeFilter)
    if (dbLinkFilter) next.set("db", dbLinkFilter)
    if (ponFilter) next.set("pon", ponFilter)
    if (statusFilter) next.set("status", statusFilter)
    if (signalFilters.length === 1) next.set("signal", signalFilters[0])
    const current = searchParams.toString()
    const upcoming = next.toString()
    if (current !== upcoming) setSearchParams(next, { replace: true })
  }, [search, oltFilter, boardFilter, portFilter, zoneFilter, odbFilter, vlanFilter, typeFilter, dbLinkFilter, ponFilter, statusFilter, signalFilters, searchParams, setSearchParams])

  const activeFilters = useMemo(() => ({
    search,
    oltFilter,
    boardFilter,
    portFilter,
    zoneFilter,
    odbFilter,
    vlanFilter,
    typeFilter,
    dbLinkFilter,
    ponFilter,
    statusFilter,
    signalFilters,
  }), [search, oltFilter, boardFilter, portFilter, zoneFilter, odbFilter, vlanFilter, typeFilter, dbLinkFilter, ponFilter, statusFilter, signalFilters])
  const optionRows = useCallback(
    (except: FilterKey) => rows.filter((row) => rowMatchesFilters(row, activeFilters, except)),
    [rows, activeFilters],
  )
  const oltOptionRows = useMemo(() => optionRows("olt"), [rows, activeFilters])
  const boards = useMemo(() => countMap(optionRows("board").map((row) => row.board)), [rows, activeFilters])
  const ports = useMemo(() => countMap(optionRows("port").map((row) => row.port)), [rows, activeFilters])
  const zones = useMemo(() => countMap(optionRows("zone").map((row) => row.zone_name)), [rows, activeFilters])
  const odbs = useMemo(() => countMap(optionRows("odb").map((row) => row.odb_name)), [rows, activeFilters])
  const vlans = useMemo(() => countMap(optionRows("vlan").map((row) => row.vlan)), [rows, activeFilters])
  const types = useMemo(() => countMap(optionRows("type").map((row) => row.onu_type_name)), [rows, activeFilters])
  const dbCounts = useMemo(() => {
    const scoped = optionRows("db")
    return {
      linked: scoped.filter((row) => row.is_in_db).length,
      not_in_db: scoped.filter((row) => !row.is_in_db).length,
    }
  }, [rows, activeFilters])

  const filtered = useMemo(() => {
    const next = rows.filter((row) => {
      return rowMatchesFilters(row, activeFilters)
    })
    next.sort((a, b) => compareRows(a, b, sortKey, sortDir))
    return next
  }, [rows, activeFilters, sortKey, sortDir])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const pageSafe = Math.min(page, totalPages)
  const paged = filtered.slice((pageSafe - 1) * pageSize, pageSafe * pageSize)

  const clearFilters = () => {
    setSearch("")
    setOltFilter("")
    setBoardFilter("")
    setPortFilter("")
    setZoneFilter("")
    setOdbFilter("")
    setVlanFilter("")
    setTypeFilter("")
    setDbLinkFilter("")
    setPonFilter("")
    setStatusFilter("")
    setSignalFilters([])
  }

  const toggleSignal = (band: SignalBand) => {
    setSignalFilters((prev) => (prev.includes(band) ? prev.filter((item) => item !== band) : [...prev, band]))
  }

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"))
      return
    }
    setSortKey(key)
    setSortDir("asc")
  }

  const handleSyncOnuMapping = async () => {
    setSyncingMapping(true)
    try {
      const res = await smartoltService.syncOnuMappings()
      const newly = res.newly_mapped || 0
      const corrected = res.corrected_mappings || 0
      const already = res.already_correct || 0
      const noMatch = res.skipped_no_match || 0

      if (newly > 0 || corrected > 0) {
        toast.success(
          `Sync complete: ${newly} new mapping${newly === 1 ? "" : "s"}, ${corrected} corrected, ${already} verified.`
        )
      } else {
        toast.info(
          `All mappings verified: ${already} already correct, ${noMatch} customer${noMatch === 1 ? "" : "s"} without SmartOLT match.`
        )
      }
      await load(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to sync ONU mappings")
    } finally {
      setSyncingMapping(false)
    }
  }

  const iconBtn = (active: boolean) =>
    `inline-flex h-9 w-9 items-center justify-center border-r border-slate-200 last:border-r-0 ${
      active ? "bg-electric-blue text-white" : "bg-white text-slate-500 hover:bg-slate-50"
    }`

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Configured ONUs/ONTs</h1>
          <p className="text-xs text-slate-500">{filtered.length} of {rows.length} ONUs</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={syncingMapping}
            onClick={handleSyncOnuMapping}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-600 px-3.5 text-sm font-semibold text-white shadow-xs transition-all hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncingMapping ? "animate-spin" : ""}`} />
            {syncingMapping ? "Syncing Mapping..." : "Sync ONU Mapping"}
          </button>
          <button
            type="button"
            disabled={refreshing}
            onClick={() => load(false, true)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-xs transition-all hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 text-slate-500 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
        <div className="grid grid-cols-2 gap-2 border-b border-slate-200 bg-slate-50/80 p-3 sm:p-4 md:flex md:flex-wrap md:items-center md:gap-2.5">
          <div className="relative col-span-2 min-w-0 md:min-w-[180px] md:flex-1 md:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search ONUs…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-xs"
            />
          </div>
            <select value={oltFilter} onChange={(e) => setOltFilter(e.target.value)} className={selectClass}>
              <option value="">All OLTs</option>
              {olts.filter((olt) => oltOptionRows.some((row) => String(row.olt_id) === String(olt.olt_id))).map((olt) => (
                <option key={olt.olt_id} value={olt.olt_id}>
                  {olt.name || olt.olt_id}
                </option>
              ))}
            </select>
            <select value={boardFilter} onChange={(e) => setBoardFilter(e.target.value)} className={selectClass}>
              <option value="">All Boards</option>
              {boards.map(([board, count]) => (
                <option key={board} value={board}>
                  {board} ({count})
                </option>
              ))}
            </select>
            <select value={portFilter} onChange={(e) => setPortFilter(e.target.value)} className={selectClass}>
              <option value="">All Ports</option>
              {ports.map(([port, count]) => (
                <option key={port} value={port}>
                  {port} ({count})
                </option>
              ))}
            </select>
            <select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} className={selectClass}>
              <option value="">All Zones</option>
              {zones.map(([zone, count]) => (
                <option key={zone} value={zone}>
                  {zone} ({count})
                </option>
              ))}
            </select>
            <select value={odbFilter} onChange={(e) => setOdbFilter(e.target.value)} className={selectClass}>
              <option value="">All ODBs</option>
              {odbs.map(([odb, count]) => (
                <option key={odb} value={odb}>
                  {odb} ({count})
                </option>
              ))}
            </select>
            <select value={vlanFilter} onChange={(e) => setVlanFilter(e.target.value)} className={selectClass}>
              <option value="">All Vlans</option>
              {vlans.map(([vlan, count]) => (
                <option key={vlan} value={vlan}>
                  {vlan} ({count})
                </option>
              ))}
            </select>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={`${selectClass} min-w-[120px]`}>
              <option value="">All ONU Type</option>
              {types.map(([type, count]) => (
                <option key={type} value={type}>
                  {type} ({count})
                </option>
              ))}
            </select>
            <select value={dbLinkFilter} onChange={(e) => setDbLinkFilter(e.target.value as DbLinkFilter)} className={`${selectClass} min-w-[120px]`}>
              <option value="">All DB Links</option>
              <option value="linked">DB Linked ({dbCounts.linked})</option>
              <option value="not_in_db">Not in DB ({dbCounts.not_in_db})</option>
            </select>
          <button type="button" onClick={() => exportCsv(filtered)} disabled={filtered.length === 0}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-50">
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
          <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
            {(["gpon", "epon"] as Exclude<PonFilter, "">[]).map((value) => (
              <button key={value} type="button" onClick={() => setPonFilter(ponFilter === value ? "" : value)}
                className={`h-9 px-3 text-xs font-semibold ${
                  ponFilter === value ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}>
                {value.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
            <button type="button" title="Online" onClick={() => setStatusFilter(statusFilter === "online" ? "" : "online")}
              className={`inline-flex h-9 w-9 items-center justify-center border-r border-slate-200 last:border-r-0 ${statusFilter === "online" ? "bg-blue-600 text-white" : "bg-white text-emerald-600 hover:bg-slate-50"}`}>
              <Globe className="h-4 w-4" />
            </button>
            <button type="button" title="Offline" onClick={() => setStatusFilter(statusFilter === "offline" ? "" : "offline")}
              className={`inline-flex h-9 w-9 items-center justify-center border-r border-slate-200 last:border-r-0 ${statusFilter === "offline" ? "bg-blue-600 text-white" : "bg-white text-slate-400 hover:bg-slate-50"}`}>
              <Globe className="h-4 w-4" />
            </button>
            <button type="button" title="LOS" onClick={() => setStatusFilter(statusFilter === "los" ? "" : "los")}
              className={`inline-flex h-9 w-9 items-center justify-center border-r border-slate-200 last:border-r-0 ${statusFilter === "los" ? "bg-blue-600 text-white" : "bg-white text-rose-600 hover:bg-slate-50"}`}>
              <Unlink className="h-4 w-4" />
            </button>
            <button type="button" title="Power fail" onClick={() => setStatusFilter(statusFilter === "power" ? "" : "power")}
              className={`inline-flex h-9 w-9 items-center justify-center border-r border-slate-200 last:border-r-0 ${statusFilter === "power" ? "bg-blue-600 text-white" : "bg-white text-amber-600 hover:bg-slate-50"}`}>
              <PlugZap className="h-4 w-4" />
            </button>
          </div>
          <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
            <button type="button" title="Strong signal" onClick={() => toggleSignal("strong")}
              className={`inline-flex h-9 w-9 items-center justify-center border-r border-slate-200 last:border-r-0 ${signalFilters.includes("strong") ? "bg-blue-600 text-white" : "bg-white text-emerald-600 hover:bg-slate-50"}`}>
              <Signal className="h-4 w-4" />
            </button>
            <button type="button" title="Medium signal" onClick={() => toggleSignal("medium")}
              className={`inline-flex h-9 w-9 items-center justify-center border-r border-slate-200 last:border-r-0 ${signalFilters.includes("medium") ? "bg-blue-600 text-white" : "bg-white text-amber-600 hover:bg-slate-50"}`}>
              <Signal className="h-4 w-4" />
            </button>
            <button type="button" title="Weak signal" onClick={() => toggleSignal("weak")}
              className={`inline-flex h-9 w-9 items-center justify-center border-r border-slate-200 last:border-r-0 ${signalFilters.includes("weak") ? "bg-blue-600 text-white" : "bg-white text-rose-600 hover:bg-slate-50"}`}>
              <Signal className="h-4 w-4" />
            </button>
          </div>
          <button type="button" title="Clear filters" onClick={clearFilters}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors shadow-xs">
            <X className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => load(false, true)} disabled={refreshing}
            className="col-span-2 inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white shadow-xs hover:bg-blue-700 disabled:opacity-50 md:col-span-1 md:ml-auto">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="md:hidden">
          <div className="divide-y divide-slate-100 bg-white">
            {paged.map((row) => {
              const kind = statusKind(row)
              const mode = wanLabel(row.wan_mode)
              return (
                <button
                  key={row.id || row.onu_external_id}
                  type="button"
                  onClick={() => navigate(SMARTOLT_PATHS.onuDetail(row.onu_external_id))}
                  className="block w-full p-3 text-left transition-colors hover:bg-slate-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <StatusIcon kind={kind} />
                        <span className="truncate text-sm font-bold text-slate-900">{displayName(row)}</span>
                      </div>
                      <div className="mt-1 truncate font-mono text-[11px] text-slate-500">{row.sn || row.mac || row.onu_external_id}</div>
                    </div>
                    {row.is_in_db ? (
                      <span className="shrink-0 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                        {row.customer_internet_id || "DB"}
                      </span>
                    ) : (
                      <span className="shrink-0 rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">No DB</span>
                    )}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                      <div className="text-[10px] font-semibold uppercase text-slate-500">Signal</div>
                      <div className="mt-0.5"><SignalCell value={row.signal_rx} /></div>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                      <div className="text-[10px] font-semibold uppercase text-slate-500">Mgmt IP</div>
                      <div className="mt-0.5 truncate font-mono font-semibold text-slate-800">{row.mgmt_ip || "-"}</div>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                      <div className="text-[10px] font-semibold uppercase text-slate-500">PON</div>
                      <div className="mt-0.5 truncate font-semibold text-slate-800">{row.pon_port || `${row.board || "-"}/${row.port || "-"}`}</div>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-2.5 py-2">
                      <div className="text-[10px] font-semibold uppercase text-slate-500">WAN</div>
                      <div className="mt-0.5 truncate font-semibold text-slate-800">{mode || row.wan_mode || "-"}</div>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {row.vlan != null ? <Badge tone="slate">VLAN {row.vlan}</Badge> : null}
                    {row.onu_type_name ? <Badge tone="blue">{row.onu_type_name}</Badge> : null}
                    {hasService(row.voip) ? <Badge tone="red">Tel</Badge> : null}
                    {hasService(row.catv_status) ? <Badge tone={isServiceOn(row.catv_status) ? "green" : "red"}>TV</Badge> : null}
                  </div>
                </button>
              )
            })}
          </div>
          {!loading && filtered.length === 0 && (
            <p className="px-3 py-10 text-center text-sm text-slate-500">
              {rows.length === 0
                ? "No configured ONUs yet. They appear after the next SmartOLT sync."
                : "No rows match the current filters."}
            </p>
          )}
          {loading && <p className="px-3 py-10 text-center text-sm text-slate-500">Loading configured ONUs...</p>}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[1380px] text-xs">
            <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold">Status</th>
                  <th className="px-3 py-2.5 text-left font-semibold">DB</th>
                  <th className="px-3 py-2.5 text-left font-semibold">
                    <button type="button" onClick={() => toggleSort("name")}>Name</button>
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold">
                    <button type="button" onClick={() => toggleSort("sn")}>SN/MAC</button>
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold">
                    <button type="button" onClick={() => toggleSort("zone")}>Zone</button>
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold">
                    <button type="button" onClick={() => toggleSort("signal")}>Signal</button>
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold">TR069</th>
                  <th className="px-3 py-2.5 text-left font-semibold">B/R</th>
                  <th className="px-3 py-2.5 text-left font-semibold">
                    <button type="button" onClick={() => toggleSort("vlan")}>VLAN</button>
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold">VoIP</th>
                  <th className="px-3 py-2.5 text-left font-semibold">TV</th>
                  <th className="px-3 py-2.5 text-left font-semibold">
                    <button type="button" onClick={() => toggleSort("type")}>Type</button>
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold">
                    <button type="button" onClick={() => toggleSort("auth")}>Auth Date</button>
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold">PON Port</th>
                  <th className="px-3 py-2.5 text-left font-semibold">
                    <button type="button" onClick={() => toggleSort("mgmt")}>Mngmnt IP</button>
                  </th>
                  <th className="px-3 py-2.5 text-left font-semibold">M-VLAN</th>
                  <th className="px-3 py-2.5 text-left font-semibold">WAN</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
          {paged.map((row) => {
            const kind = statusKind(row)
            const mode = wanLabel(row.wan_mode)
            return (
              <tr
                key={row.id || row.onu_external_id}
                onClick={() => navigate(SMARTOLT_PATHS.onuDetail(row.onu_external_id))}
                className="cursor-pointer transition-colors hover:bg-slate-50/80"
              >
                <td className="px-3 py-2.5"><StatusIcon kind={kind} /></td>
                <td className="px-3 py-2.5">
                  {row.is_in_db ? (
                    <span
                      className="inline-flex max-w-[140px] items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
                      title={row.customer_name || row.customer_internet_id || "Linked with customer DB"}
                    >
                      <span className="truncate">{row.customer_internet_id || "DB Linked"}</span>
                    </span>
                  ) : (
                    <span className="inline-flex rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                      Not in DB
                    </span>
                  )}
                </td>
                <td className="max-w-[220px] truncate px-3 py-2.5 font-semibold text-slate-900 hover:text-blue-600" title={displayName(row)}>
                  {displayName(row)}
                </td>
                <td className="px-3 py-2.5 font-mono text-slate-600">{row.sn || row.mac || "-"}</td>
                <td className="px-3 py-2.5 text-slate-600">{row.zone_name || "-"}</td>
                <td className="px-3 py-2.5"><SignalCell value={row.signal_rx} /></td>
                <td className="px-3 py-2.5 text-slate-600">{row.tr069 || ""}</td>
                <td className="px-3 py-2.5">
                  {mode ? <Badge tone={mode === "Routing" ? "green" : "slate"}>{mode}</Badge> : null}
                </td>
                <td className="px-3 py-2.5 text-slate-700 font-medium">{row.vlan ?? "-"}</td>
                <td className="px-3 py-2.5">
                  {hasService(row.voip) ? <Badge tone="red">Tel</Badge> : null}
                </td>
                <td className="px-3 py-2.5">
                  {hasService(row.catv_status) ? (
                    <Badge tone={isServiceOn(row.catv_status) ? "green" : "red"}>TV</Badge>
                  ) : null}
                </td>
                <td className="px-3 py-2.5 text-slate-600">{row.onu_type_name || "-"}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-slate-500">{formatAuthDate(row.auth_date)}</td>
                <td className="px-3 py-2.5 text-slate-600">{row.pon_port || "-"}</td>
                <td className="px-3 py-2.5 font-mono text-slate-700">{row.mgmt_ip || "-"}</td>
                <td className="px-3 py-2.5 text-slate-600">{row.mgmt_vlan ?? "-"}</td>
                <td className="px-3 py-2.5 text-slate-600">{row.wan_mode || ""}</td>
              </tr>
            )
          })}
              </tbody>
            </table>
          {!loading && filtered.length === 0 && (
            <p className="px-3 py-10 text-center text-sm text-slate-500">
              {rows.length === 0
                ? "No configured ONUs yet. They appear after the next SmartOLT sync."
                : "No rows match the current filters."}
            </p>
          )}
          {loading && <p className="px-3 py-10 text-center text-sm text-slate-500">Loading configured ONUs…</p>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/80 px-4 py-3 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <select aria-label="Rows per page" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-8 rounded-md border border-slate-200 bg-white px-2 text-slate-700 shadow-xs">
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span>Showing {filtered.length === 0 ? 0 : (pageSafe - 1) * pageSize + 1} to {Math.min(pageSafe * pageSize, filtered.length)} of {filtered.length} records</span>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-40">
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - pageSafe) <= 2)
              .reduce<number[]>((acc, n) => {
                if (acc.length && n - acc[acc.length - 1] > 1) acc.push(-n)
                acc.push(n)
                return acc
              }, [])
              .map((n) =>
                n < 0 ? (
                  <span key={`e${n}`} className="px-1 text-slate-400">…</span>
                ) : (
                  <button key={n} type="button" onClick={() => setPage(n)}
                    className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 font-medium shadow-xs ${
                      n === pageSafe
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}>
                    {n}
                  </button>
                ),
              )}
            <button type="button" disabled={pageSafe >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-40">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
